import { motion } from "framer-motion";
import { Download, Image, Shapes, Palette, AppWindow } from "lucide-react";
import { SupportLayout } from "@/components/support-layout";

const basePath = import.meta.env.BASE_URL || "/docs/";
const brandPath = `${basePath}brand/`;

interface AssetCardProps {
  name: string;
  filename: string;
  description: string;
  previewBg: "light" | "dark";
}

function AssetCard({ name, filename, description, previewBg }: AssetCardProps) {
  const filePath = `${brandPath}${filename}`;
  const extension = filename.split(".").pop()?.toUpperCase() || "FILE";

  return (
    <div className="flex flex-col border border-border rounded-lg overflow-hidden hover:border-foreground/10 transition-colors">
      {/* Preview area */}
      <div
        className={`flex items-center justify-center p-6 h-36 ${
          previewBg === "dark"
            ? "bg-foreground"
            : "bg-accent border-b border-border"
        }`}
      >
        <img
          src={filePath}
          alt={name}
          className="max-h-full max-w-full object-contain"
          loading="lazy"
        />
      </div>

      {/* Info + download */}
      <div className="flex flex-col flex-1 p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-xs font-semibold truncate">{name}</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {description}
            </p>
          </div>
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground bg-accent px-1.5 py-0.5 rounded-full">
            {extension}
          </span>
        </div>
        <a
          href={filePath}
          download={filename}
          className="mt-auto pt-2 flex items-center justify-center gap-1.5 w-full px-3 py-1.5 bg-primary !text-white text-xs font-medium rounded-md hover:bg-primary/90 transition-colors"
        >
          <Download className="w-3 h-3" />
          Download
        </a>
      </div>
    </div>
  );
}

// ── SVG Logos
const svgLogoAssets: AssetCardProps[] = [
  { name: "Logo Black", filename: "Nordex_Logo_Black.svg", description: "Full wordmark in black", previewBg: "light" },
  { name: "Logo White", filename: "Nordex_Logo_White.svg", description: "Full wordmark in white", previewBg: "dark" },
  { name: "Logo Color \u2014 Dark BG", filename: "Nordex_Logo_Color_Black.svg", description: "Color wordmark for dark backgrounds", previewBg: "light" },
  { name: "Logo Color \u2014 Light BG", filename: "Nordex_Logo_Color_White.svg", description: "Color wordmark for light backgrounds", previewBg: "dark" },
];

// ── PNG Logos
const pngLogoAssets: AssetCardProps[] = [
  { name: "Logo Black", filename: "Nordex_Logo_Black.png", description: "Full wordmark in black, PNG", previewBg: "light" },
  { name: "Logo White", filename: "Nordex_Logo_White.png", description: "Full wordmark in white, PNG", previewBg: "dark" },
  { name: "Logo Color \u2014 Dark BG", filename: "Nordex_Logo_Color_Black.png", description: "Color wordmark, dark BG, PNG", previewBg: "light" },
  { name: "Logo Color \u2014 Light BG", filename: "Nordex_Logo_Color_White.png", description: "Color wordmark, light BG, PNG", previewBg: "dark" },
];

// ── Icons (SVG)
const svgIconAssets: AssetCardProps[] = [
  { name: "Icon \u2014 White", filename: "nordex_icon_white.svg", description: "Compact N icon in white", previewBg: "dark" },
  { name: "Icon \u2014 Black", filename: "nordex_icon_black.svg", description: "Compact N icon in black", previewBg: "light" },
  { name: "X Icon \u2014 White", filename: "nordex_x_icon_white.svg", description: "Alternate X icon in white", previewBg: "dark" },
  { name: "X Icon \u2014 Black", filename: "nordex_x_icon_black.svg", description: "Alternate X icon in black", previewBg: "light" },
  { name: "Favicon", filename: "favicon.svg", description: "SVG favicon for web use", previewBg: "light" },
];

// ── Icons (PNG)
const pngIconAssets: AssetCardProps[] = [
  { name: "Icon \u2014 White", filename: "Nordex_Ico_White.png", description: "N icon in white, PNG", previewBg: "dark" },
  { name: "Icon \u2014 Black", filename: "Nordex_Ico_Black.png", description: "N icon in black, PNG", previewBg: "light" },
  { name: "Icon Color \u2014 Dark BG", filename: "Nordex_Ico_Color_Black.png", description: "Color icon, dark BG, PNG", previewBg: "light" },
  { name: "Icon Color \u2014 Light BG", filename: "Nordex_Ico_Color_White.png", description: "Color icon, light BG, PNG", previewBg: "dark" },
];

// ── App Icon
const appIconAssets: AssetCardProps[] = [
  { name: "App Icon \u2014 1500x1500", filename: "Nordex_app_icon_1500x1500.png", description: "High-res app icon for stores", previewBg: "light" },
];

// ── Brand Colors
const brandColors = [
  { name: "Nordex Gold", hex: "#C9A867" },
  { name: "Black", hex: "#000000" },
  { name: "White", hex: "#FFFFFF" },
  { name: "Gray 900", hex: "#111827" },
  { name: "Gray 600", hex: "#4B5563" },
  { name: "Gray 50", hex: "#F9FAFB" },
];

export const MediaKit = () => {
  return (
    <SupportLayout maxWidth="max-w-6xl">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <h1 className="text-3xl font-bold mb-3 tracking-tight">Media Kit</h1>
        <p className="text-[15px] leading-relaxed text-muted-foreground max-w-xl mb-6">
          Download official Nordex logos, icons, and brand assets. Use these
          resources when referencing Nordex in press, partnerships, or
          integrations.
        </p>
        <a
          href={`${brandPath}nordex-media-kit.zip`}
          download="nordex-media-kit.zip"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary !text-white text-sm font-semibold rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Download className="w-4 h-4" />
          Download Full Kit
        </a>
      </motion.div>

      {/* Usage Guidelines */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-12 p-5 border border-border rounded-lg bg-card"
      >
        <h2 className="font-semibold mb-3">Usage Guidelines</h2>
        <ul className="space-y-1.5 text-sm text-muted-foreground">
          <li>
            Use the logo as provided — do not alter colors, proportions, or add
            effects.
          </li>
          <li>
            Maintain clear space around the logo equal to at least the height of
            the "N" character.
          </li>
          <li>
            Use the black or color-on-white versions on light backgrounds, and
            white or color-on-black versions on dark backgrounds.
          </li>
          <li>
            For questions about brand usage,{" "}
            <a
              href="/contact"
              className="text-foreground underline underline-offset-2"
            >
              contact us
            </a>
            .
          </li>
        </ul>
      </motion.section>

      {/* Logos — SVG */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-12"
      >
        <h2 className="text-xl font-semibold mb-1 flex items-center gap-2">
          <Image className="w-5 h-5 text-muted-foreground" />
          Logos — SVG
        </h2>
        <p className="text-xs text-muted-foreground mb-4">
          Full wordmark logos in SVG format for crisp rendering at any size.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {svgLogoAssets.map((asset) => (
            <AssetCard key={asset.filename} {...asset} />
          ))}
        </div>
      </motion.section>

      {/* Logos — PNG */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="mb-12"
      >
        <h2 className="text-xl font-semibold mb-1 flex items-center gap-2">
          <Image className="w-5 h-5 text-muted-foreground" />
          Logos — PNG
        </h2>
        <p className="text-xs text-muted-foreground mb-4">
          Full wordmark logos in PNG format for general use.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {pngLogoAssets.map((asset) => (
            <AssetCard key={asset.filename} {...asset} />
          ))}
        </div>
      </motion.section>

      {/* Icons — SVG */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mb-12"
      >
        <h2 className="text-xl font-semibold mb-1 flex items-center gap-2">
          <Shapes className="w-5 h-5 text-muted-foreground" />
          Icons — SVG
        </h2>
        <p className="text-xs text-muted-foreground mb-4">
          Compact icon marks and favicons in SVG format.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {svgIconAssets.map((asset) => (
            <AssetCard key={asset.filename} {...asset} />
          ))}
        </div>
      </motion.section>

      {/* Icons — PNG */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="mb-12"
      >
        <h2 className="text-xl font-semibold mb-1 flex items-center gap-2">
          <Shapes className="w-5 h-5 text-muted-foreground" />
          Icons — PNG
        </h2>
        <p className="text-xs text-muted-foreground mb-4">
          Compact icon marks in PNG format.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {pngIconAssets.map((asset) => (
            <AssetCard key={asset.filename} {...asset} />
          ))}
        </div>
      </motion.section>

      {/* App Icon */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mb-12"
      >
        <h2 className="text-xl font-semibold mb-1 flex items-center gap-2">
          <AppWindow className="w-5 h-5 text-muted-foreground" />
          App Icon
        </h2>
        <p className="text-xs text-muted-foreground mb-4">
          High-resolution app icon for app stores and marketing materials.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {appIconAssets.map((asset) => (
            <AssetCard key={asset.filename} {...asset} />
          ))}
        </div>
      </motion.section>

      {/* Brand Colors */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        className="mb-12"
      >
        <h2 className="text-xl font-semibold mb-1 flex items-center gap-2">
          <Palette className="w-5 h-5 text-muted-foreground" />
          Brand Colors
        </h2>
        <p className="text-xs text-muted-foreground mb-4">
          The official Nordex color palette.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {brandColors.map((color) => (
            <div
              key={color.hex}
              className="border border-border rounded-lg overflow-hidden"
            >
              <div
                className="h-16 border-b border-border"
                style={{ backgroundColor: color.hex }}
              />
              <div className="p-2.5">
                <p className="text-xs font-semibold">{color.name}</p>
                <p className="text-[11px] text-muted-foreground font-mono mt-0.5">
                  {color.hex}
                </p>
              </div>
            </div>
          ))}
        </div>
      </motion.section>

      {/* Contact */}
      <div className="mb-12 text-center py-6 border-t border-border">
        <p className="text-sm text-muted-foreground">
          Need a specific format or have questions about brand usage?{" "}
          <a
            href="/contact"
            className="text-foreground font-medium underline underline-offset-2"
          >
            Get in touch
          </a>
        </p>
      </div>

    </SupportLayout>
  );
};
